import logger from './logger';

/**
 * Debug configuration utility functions
 * 
 * Usage example:
 * 
 * import { enableDebug, disableDebug, enableComponentDebug, disableComponentDebug } from './utils/debugConfig';
 * 
 * // Enable all debugging
 * enableDebug();
 * 
 * // Enable debugging for specific components only
 * enableComponentDebug(['Home', 'ChatBot', 'Profile']);
 * 
 * // Disable debugging for a specific component
 * disableComponentDebug('ChatBot');
 * 
 * // Disable all debugging
 * disableDebug();
 */

/**
 * Enable debug logging globally
 */
export const enableDebug = () => {
  logger.setDebugEnabled(true);
  console.log('🔍 Debug logging ENABLED globally');
};

/**
 * Disable debug logging globally
 */
export const disableDebug = () => {
  logger.setDebugEnabled(false);
  console.log('🔍 Debug logging DISABLED globally');
};

/**
 * Enable debug logging for specific components
 * @param {string|string[]} components - Component name(s) to enable debugging for
 */
export const enableComponentDebug = (components) => {
  // Get current components
  const currentComponents = [...logger.debugComponents];
  
  // Add new components
  if (Array.isArray(components)) {
    components.forEach(component => {
      if (!currentComponents.includes(component)) {
        currentComponents.push(component);
      }
    });
  } else if (typeof components === 'string' && !currentComponents.includes(components)) {
    currentComponents.push(components);
  }
  
  // Update components
  logger.setDebugComponents(currentComponents);
  
  // Enable debug if not already enabled
  if (!logger.debugEnabled) {
    logger.setDebugEnabled(true);
  }
  
  console.log(`🔍 Debug logging enabled for components: ${currentComponents.join(', ')}`);
};

/**
 * Disable debug logging for specific components
 * @param {string|string[]} components - Component name(s) to disable debugging for
 */
export const disableComponentDebug = (components) => {
  // Get current components
  let currentComponents = [...logger.debugComponents];
  
  // Remove components
  if (Array.isArray(components)) {
    currentComponents = currentComponents.filter(c => !components.includes(c));
  } else if (typeof components === 'string') {
    currentComponents = currentComponents.filter(c => c !== components);
  }
  
  // Update components
  logger.setDebugComponents(currentComponents);
  
  console.log(`🔍 Debug logging disabled for specified components. Active components: ${currentComponents.join(', ') || 'none'}`);
};

/**
 * Get the current debug configuration
 * @returns {Object} The current debug configuration
 */
export const getDebugConfig = () => {
  return {
    enabled: logger.debugEnabled,
    components: [...logger.debugComponents]
  };
};

/**
 * List all active debug components
 */
export const listDebugComponents = () => {
  console.log(`🔍 Debug status: ${logger.debugEnabled ? 'ENABLED' : 'DISABLED'}`);
  console.log(`🔍 Active debug components: ${logger.debugComponents.length ? logger.debugComponents.join(', ') : 'none'}`);
};

// Export a simple flag for conditional code blocks
export const isDebugMode = () => logger.debugEnabled; 